import { useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { scadenze as apiScadenze } from '../api/scadenziario.ts';
import type { Scadenza } from '../api/tipi-dati.ts';
import { Dialogo, RiepilogoScadenza } from './Dialogo.tsx';
import { dataBreve } from './formato.ts';

/**
 * Annullamento di una scadenza non più dovuta.
 *
 * Il motivo è obbligatorio e non è burocrazia: fra due anni, in sede di
 * verifica, "annullata" senza spiegazione è indistinguibile da una
 * dimenticanza. La scadenza resta nello storico, non viene cancellata.
 */
export function DialogoAnnullamento({
  scadenza,
  onChiudi,
  onAnnullata,
}: {
  scadenza: Scadenza;
  onChiudi: () => void;
  onAnnullata: (messaggio: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const motivoValido = motivo.trim().length >= 3;

  async function conferma() {
    setErrore(null);
    setInCorso(true);

    try {
      await apiScadenze.annulla(scadenza.id, motivo.trim());
      onAnnullata(`«${scadenza.tipo.nome}» annullata.`);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo titolo="Annulla scadenza" aperto onChiudi={onChiudi}>
      <RiepilogoScadenza
        soggetto={scadenza.soggetto.denominazione}
        soggettoEliminato={scadenza.soggetto.eliminato}
        codice={scadenza.soggetto.codice}
        reparto={scadenza.soggetto.reparto}
        tipo={scadenza.tipo.nome}
        tipoEliminato={scadenza.tipo.eliminato}
        dataPrevista={dataBreve(scadenza.dataScadenza)}
        giorni={scadenza.giorniMancanti}
      />

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <p className="rinnovo-testo">
        La scadenza viene chiusa come <strong>ANNULLATA</strong> e non genera rinnovi.
        Resta nello storico con il motivo indicato.
      </p>

      <div className="campo">
        <label htmlFor="motivo">
          Motivo <span className="obbligatorio">obbligatorio</span>
        </label>
        <textarea
          id="motivo"
          rows={4}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="es. mezzo venduto il 30/07, dipendente cessato"
          autoFocus
          required
        />
      </div>

      <div className="dialogo-azioni">
        <button
          type="button"
          className="bottone bottone-principale"
          onClick={() => void conferma()}
          disabled={inCorso || !motivoValido}
        >
          {inCorso ? 'Salvataggio…' : 'Annulla la scadenza'}
        </button>
        <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
          Torna indietro
        </button>
      </div>
    </Dialogo>
  );
}
